import { ACTIVITY_TYPE_LABELS } from '../../constants/activityTypes.js'
/**
 * Agenda — visão semanal dos agendamentos (migration 034).
 *
 * Módulo de primeiro nível desde a issue #78 (antes vivia sob o Comercial, em
 * `/comercial/agenda`). A rota antiga segue como redirect no App.jsx.
 *
 * Referência visual: `integracao-nova/crm/01-agenda-comercial.png`, adaptada à
 * linguagem do sistema (mesma gramática do dashboard de Projetos: barra de
 * ferramentas com borda inferior, faixa de indicadores, cards `radius 14` com
 * shadow-xs, painéis com título + ícone e coluna lateral sticky).
 *
 * O card "Meus links" (links públicos de agendamento) e o "Calendário
 * conectado" ficaram fora desta entrega — links públicos saíram de escopo e a
 * integração com o Google Calendar é a issue #72.
 *
 * Modelagem: um agendamento é uma `activities` com inicio_at (não há tabela de
 * eventos), então o que é criado aqui já aparece na aba "Atividades" do lead e
 * do negócio vinculado.
 *
 * A grade mostra segunda a sexta; sábado/domingo só entram na semana em que há
 * algo marcado — assim o dia útil não perde espaço, mas nenhum evento some. A
 * faixa de horas parte da disponibilidade do responsável e se estende para
 * cobrir eventos fora do expediente, pelo mesmo motivo.
 *
 * Visão "Dia" é escolha do usuário no segmented control e vira obrigatória em
 * telas estreitas, onde 5 colunas ficariam ilegíveis.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import {
  agListEvents, agListUpcoming, agGetAvailability, agUpdateEvent,
  agCalendarStatus, agCalendarSync, getCrmMembers,
} from '../../db/api.js'
import AgendaEventModal from './AgendaEventModal.jsx'
import AgendaAvailabilityModal from './AgendaAvailabilityModal.jsx'
import AgendaCalendarCard from './AgendaCalendarCard.jsx'
import './agenda.css'

const HOUR_PX = 56            // altura de 1 hora na grade (casa com o CSS)
const AUTO_SYNC_MS = 10 * 60 * 1000   // idade do último sync que dispara o automático
const DIA_MS = 86400000
const DIAS_CURTOS = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB']
const DIAS_LONGOS = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado']
const MESES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
]

// Cor por tipo de agendamento. Azul é a cor de ação do sistema, então fica com
// a reunião (o caso dominante); os outros tipos usam a paleta de séries.
const TIPO_COR = {
  evento: { dot: '#2563eb', bg: '#eff4ff', border: '#d3e2ff' },
  follow_up: { dot: '#7c5cf0', bg: '#f4f1fe', border: '#e0d8fc' },
  tarefa: { dot: '#12805c', bg: '#e7f7ee', border: '#c3e9d6' },
  mensagem: { dot: '#b54708', bg: '#fff4e5', border: '#f6ddb8' },
  pagamento: { dot: '#c93c49', bg: '#fdecef', border: '#f7cdd3' },
}
const corTipo = (tipo) => TIPO_COR[tipo] || TIPO_COR.evento

const TIPO_LABEL = { ...ACTIVITY_TYPE_LABELS, evento: 'Reunião' }

const VISTAS = [
  { id: 'semana', label: 'Semana' },
  { id: 'dia', label: 'Dia' },
]

const pad = (n) => String(n).padStart(2, '0')

/** Segunda-feira da semana da data informada (00:00 local). */
function inicioDaSemana(d) {
  const base = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const dow = base.getDay()                 // 0 = domingo
  const delta = dow === 0 ? -6 : 1 - dow    // domingo pertence à semana anterior
  base.setDate(base.getDate() + delta)
  return base
}

/** Número ISO da semana (o print mostra "Semana 33"). */
function semanaIso(d) {
  const alvo = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
  const dow = alvo.getUTCDay() || 7
  alvo.setUTCDate(alvo.getUTCDate() + 4 - dow)
  const inicioAno = new Date(Date.UTC(alvo.getUTCFullYear(), 0, 1))
  return Math.ceil(((alvo - inicioAno) / DIA_MS + 1) / 7)
}

function rotuloSemana(seg, ultimoDia) {
  const fim = new Date(seg.getTime() + (ultimoDia - 1) * DIA_MS)
  const mesmoMes = seg.getMonth() === fim.getMonth()
  const ini = mesmoMes ? `${seg.getDate()}` : `${seg.getDate()} de ${MESES[seg.getMonth()]}`
  return `${ini} – ${fim.getDate()} de ${MESES[fim.getMonth()]} de ${fim.getFullYear()}`
}

const horaMin = (d) => d.getHours() * 60 + d.getMinutes()
const fmtHora = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`
const mesmoDia = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

/** 'HH:MM' → minutos desde a meia-noite (null se vazio/inválido). */
function minutosDe(hhmm) {
  if (!hhmm) return null
  const [h, m] = String(hhmm).split(':').map(Number)
  if (Number.isNaN(h)) return null
  return h * 60 + (m || 0)
}

const iniciais = (nome) =>
  String(nome || '?').trim().split(/\s+/).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('') || '?'

export default function Agenda() {
  const { selectedClient, user, toast } = useApp()
  const clientId = selectedClient?.id || null
  const location = useLocation()
  const navigate = useNavigate()

  const [semana, setSemana] = useState(() => inicioDaSemana(new Date()))
  const [ownerId, setOwnerId] = useState(user?.id || '')
  const [members, setMembers] = useState([])
  const [events, setEvents] = useState([])
  const [upcoming, setUpcoming] = useState([])
  const [availability, setAvailability] = useState(null)
  const [calendar, setCalendar] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  const [modal, setModal] = useState(null)        // { event?, defaultStart? }
  const [availOpen, setAvailOpen] = useState(false)
  const [ownerOpen, setOwnerOpen] = useState(false)
  const [vista, setVista] = useState('semana')

  // Abaixo de 820px a grade de 5 colunas viraria uma tira ilegível: a visão de
  // dia deixa de ser opção e passa a ser o modo.
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 820px)').matches
  )
  const [diaSel, setDiaSel] = useState(() => {
    const hoje = new Date().getDay()
    return hoje === 0 || hoje === 6 ? 0 : hoje - 1   // índice na semana (0 = segunda)
  })

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const mq = window.matchMedia('(max-width: 820px)')
    const onChange = (e) => setNarrow(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    if (!clientId) return undefined
    let alive = true
    getCrmMembers()
      .then((ms) => { if (alive) setMembers(ms || []) })
      .catch(() => { /* sem lista: fica só o usuário logado no seletor */ })
    return () => { alive = false }
  }, [clientId])

  const fim = useMemo(() => new Date(semana.getTime() + 7 * DIA_MS), [semana])

  const carregar = useCallback(async () => {
    if (!clientId) { setLoading(false); return }
    setErr(null)
    try {
      const [evs, next, avail, cal] = await Promise.all([
        agListEvents({ from: semana.toISOString(), to: fim.toISOString(), ownerId: ownerId || null }),
        agListUpcoming({ ownerId: ownerId || null, limit: 5 }),
        agGetAvailability(ownerId || null),
        // O estado da conexão com o Google não pode derrubar a agenda: se a
        // rota falhar (ou a migration 035 não estiver aplicada), o card some.
        agCalendarStatus().catch(() => ({ connected: false, available: false })),
      ])
      setEvents(evs || [])
      setUpcoming(next || [])
      setAvailability(avail || null)
      setCalendar(cal || null)
    } catch (e) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }, [clientId, semana, fim, ownerId])

  useEffect(() => {
    setLoading(true)
    let alive = true
    ;(async () => {
      if (!alive) return
      await carregar()
    })()
    return () => { alive = false }
  }, [carregar])

  // Volta do consent do Google: sincroniza uma vez para o calendário não ficar
  // aparentemente vazio logo depois de conectar. `ref` porque o state da rota
  // sobrevive a re-renders até ser limpo.
  const posConexao = useRef(false)
  useEffect(() => {
    if (!location.state?.calendarConnected || posConexao.current || !clientId) return
    posConexao.current = true
    navigate(location.pathname, { replace: true, state: {} })
    ;(async () => {
      try {
        const r = await agCalendarSync()
        toast?.(
          `Google Calendar conectado. ${r.criados || 0} eventos importados, ${r.enviados || 0} enviados.`,
          'success'
        )
      } catch (e) {
        toast?.(`Conectado, mas a primeira sincronização falhou: ${e.message}`, 'error')
      } finally {
        await carregar()
      }
    })()
  }, [location.state, location.pathname, clientId, navigate, carregar, toast])

  // Sincronização automática ao abrir a agenda: sem ela, um compromisso criado
  // no Google só apareceria aqui depois de alguém lembrar de clicar em
  // "Sincronizar agora". Roda no máximo uma vez por cliente/sessão da tela e só
  // quando o último sync já está velho — abrir a agenda três vezes seguidas não
  // rende três idas ao Google. Silenciosa de propósito: erro aqui já aparece no
  // card (o vínculo guarda o `last_error`), e um toast a cada abertura viraria
  // ruído.
  const autoSyncDe = useRef(null)
  useEffect(() => {
    if (!clientId || !calendar?.connected || calendar.last_error) return
    if (autoSyncDe.current === clientId) return
    const ultimo = calendar.last_synced_at ? Date.parse(calendar.last_synced_at) : 0
    if (Number.isFinite(ultimo) && Date.now() - ultimo < AUTO_SYNC_MS) return
    autoSyncDe.current = clientId
    ;(async () => {
      try {
        await agCalendarSync()
        await carregar()
      } catch { /* estado do erro vem do próprio card na próxima carga */ }
    })()
  }, [clientId, calendar, carregar])

  // ── Dias visíveis: seg–sex + fim de semana só se houver evento ───────────
  const eventosPorDia = useMemo(() => {
    const mapa = new Map()
    for (const ev of events) {
      if (!ev.inicio_at) continue
      const ini = new Date(ev.inicio_at)
      const idx = Math.floor((ini - semana) / DIA_MS)
      if (idx < 0 || idx > 6) continue
      if (!mapa.has(idx)) mapa.set(idx, [])
      mapa.get(idx).push({
        ...ev,
        _ini: ini,
        _fim: ev.fim_at ? new Date(ev.fim_at) : new Date(ini.getTime() + 3600000),
      })
    }
    return mapa
  }, [events, semana])

  const totalDias = useMemo(
    () => (eventosPorDia.has(5) || eventosPorDia.has(6) ? 7 : 5),
    [eventosPorDia]
  )

  const dias = useMemo(
    () => Array.from({ length: totalDias }, (_, i) => new Date(semana.getTime() + i * DIA_MS)),
    [semana, totalDias]
  )

  // ── Indicadores da semana (tudo derivado dos eventos carregados) ─────────
  const hoje = new Date()
  const kpis = useMemo(() => {
    const lista = [...eventosPorDia.values()].flat()
    const minutos = lista.reduce((soma, ev) => soma + Math.max(0, (ev._fim - ev._ini) / 60000), 0)
    const feitos = lista.filter((ev) => ev.status === 'feito').length
    return {
      total: lista.length,
      horas: minutos / 60,
      hoje: lista.filter((ev) => mesmoDia(ev._ini, hoje)).length,
      feitos,
      pctFeitos: lista.length ? Math.round((feitos / lista.length) * 100) : 0,
      comLead: lista.filter((ev) => ev.contact_id || ev.deal_id).length,
    }
    // `hoje` é recriado a cada render mas só muda de valor uma vez por dia.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventosPorDia])

  // ── Faixa de horas: expediente + folga para eventos fora dele ────────────
  const [horaIni, horaFim] = useMemo(() => {
    let ini = 8
    let fim2 = 18
    const janelas = (availability?.dias || []).filter((d) => d.disponivel)
    if (janelas.length) {
      const inicios = janelas.map((d) => minutosDe(d.hora_inicio)).filter((v) => v != null)
      const fins = janelas.map((d) => minutosDe(d.hora_fim)).filter((v) => v != null)
      if (inicios.length) ini = Math.floor(Math.min(...inicios) / 60)
      if (fins.length) fim2 = Math.ceil(Math.max(...fins) / 60)
    }
    for (const lista of eventosPorDia.values()) {
      for (const ev of lista) {
        ini = Math.min(ini, ev._ini.getHours())
        fim2 = Math.max(fim2, ev._fim.getHours() + (ev._fim.getMinutes() > 0 ? 1 : 0))
      }
    }
    return [Math.max(0, ini), Math.min(24, Math.max(fim2, ini + 1))]
  }, [availability, eventosPorDia])

  const horas = useMemo(
    () => Array.from({ length: horaFim - horaIni }, (_, i) => horaIni + i),
    [horaIni, horaFim]
  )

  /** Faixas do dia FORA do expediente (o grid escurece). */
  const foraDoExpediente = useCallback((data) => {
    const dow = data.getDay() === 0 ? 7 : data.getDay()
    const regra = (availability?.dias || []).find((d) => Number(d.dia_semana) === dow)
    const abertura = horaIni * 60
    const fechamento = horaFim * 60
    if (!regra || !regra.disponivel) return [[abertura, fechamento]]
    const ini = minutosDe(regra.hora_inicio) ?? abertura
    const fim2 = minutosDe(regra.hora_fim) ?? fechamento
    const faixas = []
    if (ini > abertura) faixas.push([abertura, ini])
    if (fim2 < fechamento) faixas.push([fim2, fechamento])
    const bi = minutosDe(regra.intervalo_inicio)
    const bf = minutosDe(regra.intervalo_fim)
    if (bi != null && bf != null && bf > bi) faixas.push([bi, bf])
    return faixas
  }, [availability, horaIni, horaFim])

  const topoDe = (minutos) => ((minutos - horaIni * 60) / 60) * HOUR_PX
  const alturaDe = (min1, min2) => Math.max(24, ((min2 - min1) / 60) * HOUR_PX)

  const ownerNome = useMemo(() => {
    if (!ownerId) return 'Todos os responsáveis'
    const m = members.find((x) => x.id === ownerId)
    return m?.nome || m?.full_name || m?.email || 'Meus agendamentos'
  }, [ownerId, members])

  const irPara = (delta) => setSemana((s) => new Date(s.getTime() + delta * 7 * DIA_MS))
  const irParaHoje = () => {
    setSemana(inicioDaSemana(new Date()))
    const dow = new Date().getDay()
    setDiaSel(dow === 0 || dow === 6 ? 0 : dow - 1)
  }
  const naSemanaAtual = mesmoDia(semana, inicioDaSemana(new Date()))

  const abrirNovo = (data, hora) => {
    const inicio = new Date(data)
    inicio.setHours(hora ?? 9, 0, 0, 0)
    setModal({ defaultStart: inicio })
  }

  const aposSalvar = async (msg) => {
    setModal(null)
    await carregar()
    toast?.(msg, 'success')
  }

  // Concluir/reabrir direto do painel lateral: o caso mais comum depois da
  // reunião acontecer, sem precisar abrir o modal.
  const alternarStatus = async (ev) => {
    const novo = ev.status === 'feito' ? 'pendente' : 'feito'
    try {
      await agUpdateEvent(ev.id, { status: novo })
      await carregar()
    } catch (e) { setErr(e.message) }
  }

  // "Novo agendamento" cai em hoje quando a semana visível é a atual; nas
  // outras semanas, no primeiro dia visível.
  const diaSugerido = dias.find((d) => mesmoDia(d, hoje)) || dias[0]

  if (!clientId) {
    return (
      <Layout title="Agenda" breadcrumb={[{ label: 'Agenda' }]}>
        <div className="ag-empty">Selecione um cliente para ver a agenda.</div>
      </Layout>
    )
  }

  const modoDia = narrow || vista === 'dia'
  const diaAtivo = Math.min(diaSel, dias.length - 1)
  const diasVisiveis = modoDia ? [dias[diaAtivo] || dias[0]] : dias

  return (
    <Layout title="Agenda" breadcrumb={[{ label: 'Agenda' }]}>
      <div className="ag-wrap">
        {/* ── Barra de ferramentas ──────────────────────────────────────── */}
        <div className="ag-toolbar">
          <div className="ag-toolbar-left">
            {!narrow && (
              <div className="ag-segmented" role="group" aria-label="Modo de visualização">
                {VISTAS.map((v) => (
                  <button
                    key={v.id} type="button"
                    className={`ag-seg${vista === v.id ? ' is-active' : ''}`}
                    onClick={() => setVista(v.id)}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            )}

            <div className="ag-owner">
              <button type="button" className="ag-ctrl ag-owner-btn" onClick={() => setOwnerOpen((v) => !v)}>
                <span className="ag-owner-avatar">{iniciais(ownerNome)}</span>
                <span className="ag-owner-name">{ownerNome}</span>
                <Icon n="chevron_d" s={13} />
              </button>
              {ownerOpen && (
                <div className="ag-menu" onMouseLeave={() => setOwnerOpen(false)}>
                  <button
                    type="button" className={!ownerId ? 'is-active' : ''}
                    onClick={() => { setOwnerId(''); setOwnerOpen(false) }}
                  >
                    Todos os responsáveis
                  </button>
                  {members.map((m) => (
                    <button
                      key={m.id} type="button"
                      className={m.id === ownerId ? 'is-active' : ''}
                      onClick={() => { setOwnerId(m.id); setOwnerOpen(false) }}
                    >
                      {m.nome || m.full_name || m.email}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="ag-toolbar-right">
            <div className="ag-weeknav">
              <button
                type="button" className="ag-ctrl ag-icon-btn" onClick={() => irPara(-1)}
                title="Semana anterior" aria-label="Semana anterior"
              >
                <Icon n="chevron_l" s={15} />
              </button>
              <div className="ag-week-label">
                <span className="ag-week-range">{rotuloSemana(semana, totalDias)}</span>
                <span className="ag-week-num">Semana {semanaIso(semana)}</span>
              </div>
              <button
                type="button" className="ag-ctrl ag-icon-btn" onClick={() => irPara(1)}
                title="Próxima semana" aria-label="Próxima semana"
              >
                <Icon n="chevron_r" s={15} />
              </button>
            </div>

            <button type="button" className="ag-ctrl" onClick={irParaHoje} disabled={naSemanaAtual}>
              Hoje
            </button>
            <button type="button" className="ag-ctrl primary" onClick={() => abrirNovo(diaSugerido, 9)}>
              <Icon n="plus" s={14} sw={2} /> Novo agendamento
            </button>
          </div>
        </div>

        {err && <div className="ag-error">{err}</div>}

        {/* ── Indicadores da semana ─────────────────────────────────────── */}
        {!loading && (
          <div className="ag-kpis">
            <Kpi icon="calendar" tone="blue" value={kpis.total} label="Agendamentos na semana"
              note={kpis.comLead ? `${kpis.comLead} vinculados a lead ou negócio` : 'Nenhum vinculado a lead'} />
            <Kpi icon="clock" tone="purple" value={`${kpis.horas.toFixed(1).replace('.', ',')}h`} label="Horas ocupadas"
              note="Soma da duração dos agendamentos" />
            <Kpi icon="target" tone="orange" value={kpis.hoje} label="Hoje"
              note={kpis.hoje ? 'Compromissos para hoje' : 'Nada marcado para hoje'} />
            <Kpi icon="check" tone="green" value={kpis.feitos} label="Concluídos"
              note={`${kpis.pctFeitos}% dos agendamentos da semana`} />
          </div>
        )}

        <div className="ag-layout">
          {/* ── Grade ────────────────────────────────────────────────────── */}
          <section className="ag-card ag-gridcard" aria-label="Grade da semana">
            {modoDia && (
              <div className="ag-daytabs">
                {dias.map((d, i) => {
                  const qtd = (eventosPorDia.get(i) || []).length
                  return (
                    <button
                      key={d.toISOString()} type="button"
                      className={`ag-daytab${i === diaAtivo ? ' is-active' : ''}${mesmoDia(d, hoje) ? ' is-today' : ''}`}
                      onClick={() => setDiaSel(i)}
                    >
                      <span>{DIAS_CURTOS[d.getDay()]}</span>
                      <b>{d.getDate()}</b>
                      {qtd > 0 && <i className="ag-daytab-dot" />}
                    </button>
                  )
                })}
              </div>
            )}

            <div className="ag-grid-scroll">
              <div
                className="ag-grid"
                style={{ gridTemplateColumns: `60px repeat(${diasVisiveis.length}, minmax(132px, 1fr))` }}
              >
                <div className="ag-grid-corner" />
                {diasVisiveis.map((d) => (
                  <div key={`h-${d.toISOString()}`} className={`ag-dayhead${mesmoDia(d, hoje) ? ' is-today' : ''}`}>
                    <span className="ag-dayhead-dow">{modoDia ? DIAS_LONGOS[d.getDay()] : DIAS_CURTOS[d.getDay()]}</span>
                    <span className="ag-dayhead-num">{d.getDate()}</span>
                  </div>
                ))}

                <div className="ag-hours" style={{ height: horas.length * HOUR_PX }}>
                  {horas.map((h) => (
                    <div key={h} className="ag-hour" style={{ height: HOUR_PX }}>
                      <span>{pad(h)}:00</span>
                    </div>
                  ))}
                </div>

                {diasVisiveis.map((d) => {
                  const idx = Math.round((d - semana) / DIA_MS)
                  const doDia = (eventosPorDia.get(idx) || []).slice().sort((a, b) => a._ini - b._ini)
                  return (
                    <div key={`c-${d.toISOString()}`} className="ag-daycol" style={{ height: horas.length * HOUR_PX }}>
                      {foraDoExpediente(d).map(([a, b], i) => (
                        <div
                          key={`off-${i}`} className="ag-offhours"
                          style={{ top: topoDe(a), height: Math.max(0, topoDe(b) - topoDe(a)) }}
                        />
                      ))}
                      {horas.map((h) => (
                        <button
                          key={`slot-${h}`} type="button" className="ag-slot"
                          style={{ top: (h - horaIni) * HOUR_PX, height: HOUR_PX }}
                          onClick={() => abrirNovo(d, h)}
                          aria-label={`Agendar ${pad(h)}:00 de ${d.getDate()}/${pad(d.getMonth() + 1)}`}
                        >
                          <span className="ag-slot-plus"><Icon n="plus" s={12} sw={2.4} /></span>
                        </button>
                      ))}
                      {mesmoDia(d, hoje) && horaMin(hoje) >= horaIni * 60 && horaMin(hoje) <= horaFim * 60 && (
                        <div className="ag-now" style={{ top: topoDe(horaMin(hoje)) }} />
                      )}
                      {doDia.map((ev) => {
                        const cor = corTipo(ev.tipo)
                        const ini = Math.max(horaMin(ev._ini), horaIni * 60)
                        const f = Math.min(horaMin(ev._fim) || (ini + 60), horaFim * 60)
                        const altura = alturaDe(ini, Math.max(f, ini + 15))
                        const vinculo = ev.contact_nome || ev.deal_titulo
                        const doGoogle = ev.origem === 'google_calendar'
                        return (
                          <button
                            key={ev.id} type="button"
                            className={`ag-event${ev.status === 'feito' ? ' is-done' : ''}${altura < 44 ? ' is-compact' : ''}${doGoogle ? ' is-external' : ''}`}
                            style={{
                              top: topoDe(ini), height: altura,
                              background: cor.bg, borderColor: cor.border, '--ag-accent': cor.dot,
                            }}
                            onClick={() => setModal({ event: ev })}
                            title={`${fmtHora(ev._ini)}–${fmtHora(ev._fim)} · ${ev.titulo}${vinculo ? ` · ${vinculo}` : ''}${doGoogle ? ' · do Google Calendar' : ''}`}
                          >
                            <span className="ag-event-time">
                              {fmtHora(ev._ini)} – {fmtHora(ev._fim)}
                              {doGoogle && (
                                <span className="ag-event-src" title="Veio do Google Calendar">
                                  <Icon n="refresh" s={9} sw={2.4} />
                                </span>
                              )}
                            </span>
                            <span className="ag-event-title">
                              {ev.titulo}{vinculo ? <em> · {vinculo}</em> : null}
                            </span>
                            {altura >= 66 && (
                              <span className="ag-event-tag">
                                <i /> {doGoogle ? 'Google Calendar' : (TIPO_LABEL[ev.tipo] || 'Reunião')}
                              </span>
                            )}
                          </button>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </div>

            {!loading && !events.length && (
              <div className="ag-grid-empty">
                <span className="ag-grid-empty-icon"><Icon n="calendar" s={18} /></span>
                <div>
                  <div className="ag-grid-empty-title">Nenhum agendamento nesta semana</div>
                  <div className="ag-grid-empty-hint">
                    Clique em um horário livre da grade ou use <b>Novo agendamento</b>. Reuniões
                    criadas a partir de um lead ou negócio também aparecem aqui.
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* ── Coluna lateral ───────────────────────────────────────────── */}
          <aside className="ag-side">
            <div className="ag-card ag-panel">
              <div className="ag-panel-title">
                <Icon n="calendar" s={15} c="#2563eb" /> Minha disponibilidade
              </div>
              <AvailabilityResumo availability={availability} />
              <button type="button" className="ag-link" onClick={() => setAvailOpen(true)}>
                Editar disponibilidade
              </button>
            </div>

            <AgendaCalendarCard
              status={calendar}
              clientId={clientId}
              onChanged={carregar}
              onSynced={carregar}
            />

            <div className="ag-card ag-panel">
              <div className="ag-panel-title">
                <Icon n="clock" s={15} c="#7c5cf0" /> Próximos agendamentos
              </div>
              {loading ? (
                <div className="ag-muted">Carregando…</div>
              ) : !upcoming.length ? (
                <div className="ag-muted">Nada agendado por enquanto.</div>
              ) : (
                <div className="ag-next-list">
                  {upcoming.map((ev) => {
                    const ini = new Date(ev.inicio_at)
                    const cor = corTipo(ev.tipo)
                    const origem = ev.contact_nome
                      ? { label: ev.contact_nome, icon: 'leads' }
                      : (ev.deal_titulo ? { label: ev.pipeline_nome || ev.deal_titulo, icon: 'sales' } : null)
                    return (
                      <div key={ev.id} className="ag-next">
                        <span className="ag-next-avatar" style={{ background: cor.bg, color: cor.dot }}>
                          {iniciais(ev.contact_nome || ev.deal_titulo || ev.titulo)}
                        </span>
                        <button type="button" className="ag-next-main" onClick={() => setModal({ event: ev })}>
                          <span className="ag-next-title">{ev.titulo}</span>
                          <span className="ag-next-when">
                            {DIAS_CURTOS[ini.getDay()].toLowerCase()} {pad(ini.getDate())}/{pad(ini.getMonth() + 1)} · {fmtHora(ini)}
                          </span>
                          {origem && (
                            <span className="ag-next-badge">
                              <Icon n={origem.icon} s={10} /> {origem.label}
                            </span>
                          )}
                        </button>
                        <button
                          type="button"
                          className={`ag-next-check${ev.status === 'feito' ? ' is-done' : ''}`}
                          onClick={() => alternarStatus(ev)}
                          title={ev.status === 'feito' ? 'Reabrir' : 'Marcar como feito'}
                        >
                          <Icon n="check" s={12} sw={2.6} />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {modal && (
        <AgendaEventModal
          event={modal.event || null}
          defaultStart={modal.defaultStart || null}
          members={members}
          onClose={() => setModal(null)}
          onSaved={() => aposSalvar(modal.event ? 'Agendamento atualizado.' : 'Agendamento criado.')}
          onDeleted={() => aposSalvar('Agendamento excluído.')}
        />
      )}

      {availOpen && (
        <AgendaAvailabilityModal
          availability={availability}
          userId={ownerId || null}
          onClose={() => setAvailOpen(false)}
          onSaved={(a) => { setAvailability(a); setAvailOpen(false); toast?.('Disponibilidade salva.', 'success') }}
        />
      )}
    </Layout>
  )
}

/** Card de indicador — mesmo formato dos KPIs do dashboard de Projetos. */
function Kpi({ icon, tone, value, label, note }) {
  return (
    <div className="ag-card ag-kpi">
      <div className="ag-kpi-top">
        <span className={`ag-kpi-icon is-${tone}`}><Icon n={icon} s={18} /></span>
        <div style={{ minWidth: 0 }}>
          <div className="ag-kpi-value">{value}</div>
          <div className="ag-kpi-label">{label}</div>
        </div>
      </div>
      {note && <div className="ag-kpi-note">{note}</div>}
    </div>
  )
}

/** Resumo textual do expediente: agrupa os dias com o mesmo horário. */
function AvailabilityResumo({ availability }) {
  const dias = (availability?.dias || []).filter((d) => d.disponivel)
  if (!dias.length) {
    return <div className="ag-muted">Nenhum dia de expediente configurado.</div>
  }
  const ordenados = dias.slice().sort((a, b) => a.dia_semana - b.dia_semana)
  const primeiro = ordenados[0]
  const ultimo = ordenados[ordenados.length - 1]
  const mesmoHorario = ordenados.every(
    (d) => d.hora_inicio === primeiro.hora_inicio && d.hora_fim === primeiro.hora_fim
  )
  const faixaDias = mesmoHorario && ordenados.length > 1
    ? `${DIAS_LONGOS[primeiro.dia_semana % 7]} a ${DIAS_LONGOS[ultimo.dia_semana % 7]}`.toLowerCase()
    : ordenados.map((d) => DIAS_LONGOS[d.dia_semana % 7].slice(0, 3)).join(', ').toLowerCase()
  const intervalo = ordenados.find((d) => d.intervalo_inicio && d.intervalo_fim)

  return (
    <div className="ag-avail-resumo">
      <div className="ag-avail-item">
        <span className="ag-avail-icon is-blue"><Icon n="calendar" s={15} /></span>
        <div>
          <div className="ag-avail-label">{faixaDias.charAt(0).toUpperCase() + faixaDias.slice(1)}</div>
          <div className="ag-avail-value">
            {mesmoHorario ? `${primeiro.hora_inicio || '—'} – ${primeiro.hora_fim || '—'}` : 'Horários variados'}
          </div>
        </div>
      </div>
      {intervalo && (
        <div className="ag-avail-item">
          <span className="ag-avail-icon is-purple"><Icon n="clock" s={15} /></span>
          <div>
            <div className="ag-avail-label">Intervalo padrão</div>
            <div className="ag-avail-value">{intervalo.intervalo_inicio} – {intervalo.intervalo_fim}</div>
          </div>
        </div>
      )}
    </div>
  )
}
